import {call, put, select} from '@redux-saga/core/effects';
import api from 'api';
import ApiService from 'api/ApiService';
import {DialogResult, DialogTypes} from 'common/names';
import {ICloseSubDialogAction} from 'features/layout/redux/actions';
import {pushErrorToast, pushSuccessToast} from 'features/notifications/redux/actions';
import i18n from 'i18n';
import {asyncSagaConstructor, unhandedSagaError} from 'redux/common';
import {openDialogWithResult} from 'redux/common/saga/openDialogWithResult';
import {Action} from 'types/redux';
import {Transport} from 'types/transport/Abstract/AbstractTransport';
import {keyTypes} from '..';
import {selectDialogSaga} from 'redux/common/saga/selectDialogSaga';
import {layoutSelectors} from 'features/layout/redux';
import {getName} from 'redux/common/saga/getName';
import {escapeFileName} from 'common/stringUtils';
import {castToSend} from 'common/yup/castToSend';
import {getTransportSchemaByType} from 'types/transport/Transport';

const PUBLIC_TITLE = 'Upload OpenPGP Public Key To';
const SECRET_TITLE = 'Upload OpenPGP Key Pair To';
const SSH_PUBLIC_TITLE = 'Upload SSH Client Public Key To';
const SSH_SECRET_TITLE = 'Upload SSH Secret Key To';

const ASC_EXT = '.asc';
const PUBLIC_EXT = '.pub';
const SECRET_SSH_EXT = '.key';

type IHandler = {
   title: string;
   errTitle: string;
   buildFilename: (s: string) => string;
   url: string;
   // schema: ObjectSchema<any>;
};

const handlers: Record<string, IHandler> = {
   public: {
      title: PUBLIC_TITLE,
      errTitle: i18n.t('Error Uploading Public Key'),
      buildFilename: (name: string) => escapeFileName(name) + ASC_EXT,
      url: api.uploadRemotePgpPublic,
      // schema: ExportPgpPublicPayload,
   },
   secret: {
      title: SECRET_TITLE,
      errTitle: i18n.t('Error Uploading Key Pair'),
      buildFilename: (name: string) => escapeFileName(name) + ASC_EXT,
      url: api.uploadRemotePgpSecret,
      // schema: ExportPgpSecretPayload,
   },
   publicSsh: {
      title: SSH_PUBLIC_TITLE,
      errTitle: i18n.t('Error Uploading SSH Public Key'),
      buildFilename: (name: string) => escapeFileName(name) + PUBLIC_EXT,
      url: api.uploadRemoteSshPublic,
      // schema: ExportSshPublicPayload,
   },
   secretSsh: {
      title: SSH_SECRET_TITLE,
      errTitle: i18n.t('Error Uploading SSH Key Pair'),
      buildFilename: (name: string) => escapeFileName(name) + SECRET_SSH_EXT,
      url: api.uploadRemoteSshSecret,
      // schema: ExportSshSecretPayload,
   },
};

export type UploadRemoteKeyPayload = {
   filePath: string;
   transport: Transport;
   partnerId: string;
   secret?: boolean;
   ssh?: boolean;
   onUpdate: () => void;
   setLoading: (state: boolean) => void;
};

export function* uploadRemoteKey(action: Action<UploadRemoteKeyPayload>) {
   const {
      filePath: path,
      secret,
      ssh,
      transport: _transport,
      partnerId,
      onUpdate,
      setLoading,
   } = action.payload;
   let handler = handlers.public;

   const keys = (yield select(layoutSelectors.keysSelector)) as ReturnType<
      typeof layoutSelectors.keysSelector
   >;
   let options = keys.openpgp?.publicKeys;

   if (ssh) {
      handler = secret ? handlers.secretSsh : handlers.publicSsh;
      options = secret ? keys.ssh?.clientKeys : keys.ssh?.clientKeys; // TODO where secret?
   } else {
      handler = secret ? handlers.secret : handlers.public;
      options = [...(keys.openpgp?.secretKeys || []), ...keys.openpgp?.publicKeys]; // secret ? keys.openpgp?.secretKeys : keys.openpgp?.publicKeys;
   }
   const {errTitle, url} = handler;
   // let passphrase: string;
   let keyId: string;
   let fileName: string;

   const schema = getTransportSchemaByType(_transport.transportType);
   const transport = castToSend(schema, _transport);

   try {
      // if (secret) {
      //    passphrase = yield call(
      //       getPassword,
      //       i18n.t('Passphrase'),
      //       i18n.t('Enter File Passphrase')
      //    );
      //    if (!passphrase) {
      //       return;
      //    }
      // }
      setLoading(true);

      keyId = (yield call(
         selectDialogSaga,
         i18n.t('Select key'),
         i18n.t('Select key'),
         options
      )) as string;

      if (!keyId) return;

      fileName = options.find((o) => o.id === keyId)?.name || '';

      fileName = yield call(
         getName,
         i18n.t('Set the key name'),
         i18n.t('Name'),
         handler.buildFilename(fileName)
      );

      if (!fileName) return;
      fileName = escapeFileName(fileName);

      yield call(
         asyncSagaConstructor(url, keyTypes.UPLOAD_REMOTE_KEY, {
            getMethod: ApiService.postSecureJson,
            data: {
               // asciiArmored: path?.indexOf(ASC_EXT) > 0,
               path,
               overwrite: false,
               // passphrase: passphrase!,
               keyId,
               fileName,
               transport,
               partnerId,
            },
         })
      );

      yield put(
         pushSuccessToast(`Key ${fileName} successfully uploaded to
      ${path ?? 'selected folder'}`)
      );
      yield onUpdate();
   } catch (err) {
      // const e = yield err.clone().text();
      console.error(err);
      const e = JSON.stringify(err);
      // if (e.indexOf('already exists') >= 0) {
      //    // TODO, need ability to give a different name
      //    yield call(overwriteFlow, url, path, transport, partnerId, keyId!, fileName!);
      // } else {
      //    yield put(pushErrorToast(errTitle));
      // }
      //TODO
   } finally {
      setLoading(false);
   }
}

function* overwriteFlow(
   url: string,
   // schema: ObjectSchema<any>,
   path: string,
   transport: Transport,
   partnerId: string,
   keyId: string,
   fileName: string
) {
   try {
      const dialogResult: ICloseSubDialogAction<null> = yield call(openDialogWithResult, {
         id: DialogTypes.ConfirmationDialog.id,
         title: i18n.t('Key File Already Exists'),
         content: i18n.t('Do you want to replace it?'),
      });

      const shouldOverwrite = dialogResult.payload?.result === DialogResult.OK;

      if (shouldOverwrite) {
         yield call(
            asyncSagaConstructor(url, keyTypes.UPLOAD_REMOTE_KEY, {
               getMethod: ApiService.postSecureJson,
               data: {
                  // asciiArmored: path?.indexOf(ASC_EXT) > 0,
                  path,
                  overwrite: true,
                  keyId,
                  fileName,
                  transport,
                  partnerId,
               },
            })
         );
         yield put(
            pushSuccessToast(`Key ${fileName} successfully uploaded to
      ${path}`)
         );
      }
   } catch (e) {
      yield call(unhandedSagaError, e as Error);
   }
}
